import { FC, ReactChild, ReactNode, forwardRef, useState, useEffect } from 'react'
import { DialogPortal } from 'components/DialogPortal/DialogPortal'
import { ReactComponent as CloseIcon } from 'images/Icon_Close.svg'
import useKeyPress from 'hooks/useKeyPress'
import { useDispatch, useSelector } from 'react-redux'
import { getIsActiveTrap, setActiveTrap } from 'redux/reducers/modal'
import FocusTrap from 'focus-trap-react'
import classNames from 'classnames'

// This can be used in the future for more specificity in event tracking, etc
export enum ModalCloseReason {
  BackdropClick = 'backdropClick',
  EscapeKey = 'escapeKey',
  CloseButton = 'closeButton'
}

export interface MyDialogProps {
  /**
   * The container id for the dialog - must begin with 'medicare-my' to be prefixed properly
   */
  className?: string
  containerId: string
  onClose: (reason: ModalCloseReason) => void
  closeOnBackdropClick?: boolean
  heading: string
  headingClassName?: string
  closeButtonAriaLabel: string
  children: ReactChild
  actionsChildren?: ReactNode
  width?: 'narrow' | 'wide' | 'full'
}

export const MyDialogContent = forwardRef<
  HTMLDivElement,
  Omit<MyDialogProps, 'containerId'>
>(function (props, ref) {
  const {
    className,
    onClose,
    heading,
    headingClassName = 'ds-text-heading--3xl ds-u-margin-bottom--2',
    closeButtonAriaLabel,
    children,
    actionsChildren,
    closeOnBackdropClick,
    width = 'wide'
  } = props

  const widthClass = `ds-c-dialog--${width}`

  return (
    <div ref={ref}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        onClick={
          closeOnBackdropClick
            ? () => onClose(ModalCloseReason.BackdropClick)
            : undefined
        }
        data-testid="dialog-backdrop"
        className="ds-c-dialog-wrap"
        aria-labelledby="dialog-heading"
        role="dialog"
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          onClick={
            closeOnBackdropClick
              ? (e) => {
                  e.stopPropagation()
                }
              : undefined
          }
          className={classNames('ds-c-dialog my-c-mydialog', widthClass, className)}
          role="document"
        >
          <header className="ds-c-dialog__header" role="banner">
            <h1 className={headingClassName} id="dialog-heading">
              {heading}
            </h1>
            <button
              className="ds-c-button ds-c-button--ghost my-c-mydialog__close my-u-p--2"
              aria-label={closeButtonAriaLabel}
              onClick={() => onClose(ModalCloseReason.CloseButton)}
              type="button"
            >
              <CloseIcon />
            </button>
          </header>
          <main role="main">
            {children}
          </main>
          {actionsChildren && (
            <aside className="ds-c-dialog__actions">{actionsChildren}</aside>
          )}
        </div>
      </div>
    </div>
  )
})

/**
 * @param props
 * @returns a dialog component in a react portal.
 * In order to have proper styling the containerId prop must start with "medicare-my"
 */
export const MyDialog: FC<MyDialogProps> = ({ containerId, ...props }) => {
  const { onClose } = props

  useKeyPress('Escape', () => onClose(ModalCloseReason.EscapeKey))

  // Active trap Status's
  const dispatch = useDispatch()
  const deactivateTrap = () => {
    dispatch(setActiveTrap({ isActiveTrap: false }))
  }

  const isActiveTrap = useSelector(getIsActiveTrap)
  const [trapActive, setTrapActive] = useState(false)

  useEffect(() => setTrapActive(Boolean(isActiveTrap)), [isActiveTrap])

  return (
    <DialogPortal containerId={containerId}>
      <FocusTrap
        focusTrapOptions={{
          onDeactivate: deactivateTrap
        }}
        active={trapActive}
      >
        <MyDialogContent {...props} />
      </FocusTrap>
    </DialogPortal>
  )
}
